import { createContext, useCallback, useContext } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '@/contexts/session-context';
import { useFetch } from '@/hooks/use-fetch';
import { useApiQueryWithTransport } from '@/hooks/use-api-query';
import { qk } from '@/lib/query/keys';

/**
 * Phase 114 (Strategy B): the WorkOS-side `user.email` (exposed via
 * useSession().user.email — itself sourced from the sealed-session cookie
 * payload set by backend /auth/me) swaps to the new email immediately when
 * the user starts an email change — but the new address is unverified.
 * To keep "verified email visible, pending email separate," we expose a
 * second source of truth fetched from our DB at `GET /auth/profile`.
 * Components that render the user's identity (dropdown chrome, edit dialog)
 * read from THIS context, not from useSession.
 */
export interface DbProfile {
  publicId: string;
  email: string | null;
  pendingEmail: string | null;
  firstName: string | null;
  lastName: string | null;
  emailVerified: boolean;
}

interface ProfileContextValue {
  profile: DbProfile | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextValue>({
  profile: null,
  loading: true,
  refresh: async () => {},
});

export function ProfileProvider({ children }: { children: ReactNode }) {
  const { user: authUser } = useSession();
  const queryClient = useQueryClient();

  // Batch E (D8 adapter): the profile read flows through TanStack Query so the
  // edit-profile dialog's mutations can invalidate qk.user('profile') and this
  // context (which feeds the header + edit dialog) refreshes. ProfileProvider is
  // always nested inside WorkspaceProvider (App.tsx), so useFetch() is safe as
  // the transport here — it preserves the existing GET 401 sign-in routing.
  // Query's default (throwOnError: false) keeps this fail-soft: an error is held
  // on the query, `profile` falls back to null, exactly as the old try/finally.
  const profileQuery = useApiQueryWithTransport<DbProfile>(
    qk.user('profile'),
    '/auth/profile',
    useFetch(),
    { enabled: !!authUser?.id },
  );

  const profile = profileQuery.data ?? null;
  // isLoading is false for a disabled query (no user), matching the old
  // "no user → loading false" branch, and true only while actually fetching.
  const loading = profileQuery.isLoading;

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: qk.user('profile') });
  }, [queryClient]);

  return (
    <ProfileContext.Provider value={{ profile, loading, refresh }}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  return useContext(ProfileContext);
}
